import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Alert,
  Button,
  Modal,
} from "react-bootstrap";
import {
  IconBuilding,
  IconUsers,
  IconWorld,
  IconCopy,
  IconExternalLink,
  IconCheck,
  IconX,
  IconStar,
  IconGlobe,
  IconShield,
  IconPlus,
  IconSparkles,
  IconPalette,
  IconEdit,
} from "@tabler/icons-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import Swal from "sweetalert2";
import jwt_decode from "jwt-decode";
import { setCookie } from "nookies";
import { useNavigate } from "react-router-dom";
import OrganizationService from "./services/OrganizationServices";
import UserContext from "../../contexts/UserContext";
import CreateOrganization from "./modals/CreateOrganization";
import OrganizationBranding from "./modals/OrganizationBranding";
import EditOrganizationModal from "./modals/EditOrganizationModal";

const OrganizationOverview = () => {
  const navigate = useNavigate();
  const { isOrganization, getOrganizationInfo, user, getUserPlan, accountContext, accountContextLoaded } =
    useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [organizationData, setOrganizationData] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Verificar se o usuário tem acesso ao módulo
  const hasModuleAccess = () => {
    const allowedPlans = [2, 3, 4, 5, 9, 10, 11];
    return user?.plan_id && allowedPlans.includes(user.plan_id);
  };

  // Verificar se é plan_id = 3 ou 4 (planos restritos para White Label)
  const isRestrictedPlan = () => {
    return [3, 4].includes(user?.plan_id);
  };

  useEffect(() => {
    // Aguardar accountContext estar carregado antes de tentar ler dados da organização
    if (!accountContextLoaded) return;

    if (isOrganization()) {
      const organization = getOrganizationInfo();
      if (organization?.id) {
        loadOrganizationDetails(organization.id);
      }
    }
  }, [accountContextLoaded, accountContext]);

  // Verificar se o usuário pode criar organização
  const canCreateOrganization = () => {
    // Usuário com plan_id = 2 ou 5 que ainda não possui organização
    const allowedPlans = [2, 3, 4, 5, 9, 10, 11];
    const hasValidPlan = user?.plan_id && allowedPlans.includes(user.plan_id);

    // Se isOrganization retorna true mas organization?.id não existe = pode criar
    const organizationInfo = getOrganizationInfo();
    const canCreate = isOrganization() && !organizationInfo?.id;

    return hasValidPlan && (!isOrganization() || canCreate);
  };

  // Handler para sucesso na criação da organização
  const handleOrganizationCreated = (response) => {
    setShowCreateModal(false);

    // Verificar se foi retornado um novo token
    if (response?.access_token) {
      console.log("✅ Novo token recebido após criação da organização");

      // Salvar o novo token no cookie (substituindo o atual)
      setCookie(null, "token", response.access_token, {
        maxAge: 60 * 60 * 24 * 7, // 7 dias
        path: "/",
      });

      // Salvar também no localStorage se necessário
      localStorage.setItem("token", response.access_token);

      let token = jwt_decode(response.access_token);

      if (token?.account_context) {
        sessionStorage.setItem(
          "account_context",
          JSON.stringify(token.account_context)
        );
      }

      // Armazenar features da conta
      if (token?.features) {
        sessionStorage.setItem(
          "account_features",
          JSON.stringify(token.features)
        );
      }

      // Mostrar mensagem de sucesso
      Swal.fire({
        icon: "success",
        title: "🎉 Organização Criada com Sucesso!",
        html: `
          <div style="text-align: center; padding: 20px;">
            <p style="margin-bottom: 15px;">Sua organização <strong>${response.name}</strong> foi criada!</p>
            <p style="color: #6c757d; font-size: 14px; margin-bottom: 20px;">
              Recarregando a página para aplicar as atualizações...
            </p>
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        timer: 3000,
      }).then(() => {
        // Reload após 3 segundos
        window.location.reload();
      });

      // Backup: forçar reload após 3 segundos mesmo se o SweetAlert falhar
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } else {
      // Fallback: reload simples se não houver novo token
      console.log("⚠️ Nenhum token retornado, fazendo reload simples");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  const loadOrganizationDetails = async (organizationId) => {
    try {
      setLoading(true);
      const orgData = await OrganizationService.getOrganization(organizationId);
      console.log(orgData);
      setOrganizationData(orgData);
    } catch (error) {
      console.error("Erro ao carregar detalhes:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os detalhes da organização.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler para sucesso na edição dos nomes da organização
  const handleOrganizationUpdated = () => {
    setShowEditModal(false);
    if (organizationData?.id) {
      loadOrganizationDetails(organizationData.id);
    }
  };

  // Copiar URL para clipboard
  const copyUrlToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    Swal.fire({
      icon: "success",
      title: "Copiado!",
      text: "URL copiada para a área de transferência",
      timer: 2000,
      showConfirmButton: false,
      toast: true,
      position: "top-end",
    });
  };

  // Abrir URL externa
  const openExternalUrl = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Renderizar status
  const renderStatus = (status) => {
    const statusMap = {
      1: { variant: "success", text: "Ativa" },
      0: { variant: "secondary", text: "Inativa" },
      2: { variant: "warning", text: "Suspensa" },
    };
    const statusInfo = statusMap[status] || {
      variant: "secondary",
      text: "Desconhecido",
    };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  // Verificar acesso ao módulo primeiro
  if (!hasModuleAccess()) {
    return (
      <Alert variant="warning" className="organizations-dashboard__alert">
        <IconBuilding size={16} className="me-2" />
        Esta funcionalidade está disponível apenas para planos específicos.
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="organizations-dashboard__loading">
        <div className="organizations-dashboard__loading-content">
          <Spinner animation="border" variant="primary" />
          <p className="organizations-dashboard__loading-text">
            Carregando detalhes da organização...
          </p>
        </div>
      </div>
    );
  }

  // Se não é organização OU se é organização mas sem ID (pode criar), verificar se pode criar uma
  if (!isOrganization() || (isOrganization() && !getOrganizationInfo()?.id)) {
    if (canCreateOrganization()) {
      // Estado especial: usuário pode criar organização
      return (
        <div
          style={{
            minHeight: "100vh",
          }}
        >
          {/* Header Hero Section para Criar Organização */}
          <div
            className="mb-5 p-4 text-white rounded"
            style={{
              background: "var(--gradient-highlight)",
            }}
          >
            <Container>
              <Row className="align-items-center justify-content-center text-center">
                <Col md={8}>
                  <h1
                    className="mb-3 d-flex align-items-center justify-content-center"
                    style={{ fontSize: "2.5rem", fontWeight: 700 }}
                  >
                    <IconSparkles size={40} className="me-3" />
                    Crie sua Organização{" "}
                    {[2, 5, 9, 10, 11].includes(getUserPlan()) ? "White Label" : ""}
                  </h1>
                  <p
                    className="mb-4"
                    style={{ fontSize: "1.2rem", opacity: 0.9 }}
                  >
                    Crie sua organização para convidar empresas para usar sua
                    plataforma.
                  </p>
                  <Button
                    variant="light"
                    size="lg"
                    onClick={() => setShowCreateModal(true)}
                    className="shadow"
                    style={{
                      fontWeight: 600,
                      padding: "12px 32px",
                      borderRadius: "12px",
                    }}
                  >
                    <IconPlus size={20} className="me-2" />
                    Criar Organização
                  </Button>
                </Col>
              </Row>
            </Container>
          </div>

          {/* Cards Section com Benefícios */}

          <Container>
            <Row>
              {/* Card de Marca Própria - Apenas para planos 2 ou 5 */}
              {[2, 5, 9, 10, 11].includes(getUserPlan()) && (
                <Col xs={12} md={6} lg={4} className="mb-4">
                  <Card
                    className="h-100 shadow-sm border-0"
                    style={{ borderRadius: "15px", overflow: "hidden" }}
                  >
                    <div
                      style={{
                        height: "4px",
                        background: "var(--gradient-primary)",
                        borderRadius: "15px 15px 0 0",
                      }}
                    ></div>
                    <Card.Body className="p-4 text-center">
                      <div className="mb-3">
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            background:
                              "var(--gradient-primary)",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            boxShadow: "0 4px 12px rgba(48, 92, 222, 0.3)",
                            margin: "0 auto",
                          }}
                        >
                          <IconBuilding size={24} />
                        </div>
                      </div>
                      <h5 className="fw-bold mb-3">Marca Própria</h5>
                      <p className="text-muted mb-0">
                        Personalize cores, logo e domínio com sua identidade
                        visual. Crie uma experiência única para seus clientes.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Card de Múltiplas Empresas */}
              <Col xs={12} md={6} lg={4} className="mb-4">
                <Card
                  className="h-100 shadow-sm border-0"
                  style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                  <div
                    style={{
                      height: "4px",
                      background: "linear-gradient(135deg, #2bc155, #20c997)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  ></div>
                  <Card.Body className="p-4 text-center">
                    <div className="mb-3">
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          background:
                            "linear-gradient(135deg, #2bc155, #20c997)",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          boxShadow: "0 4px 12px rgba(43, 193, 85, 0.3)",
                          margin: "0 auto",
                        }}
                      >
                        <IconUsers size={24} />
                      </div>
                    </div>
                    {getUserPlan() === 3 ? (
                      <h5 className="fw-bold mb-3">1 Empresa</h5>
                    ) : (
                      <h5 className="fw-bold mb-3">Múltiplas Empresas</h5>
                    )}

                    <p className="text-muted mb-0">
                      Convide {getUserPlan() === 3 ? "1 empresa" : "empresas"}{" "}
                      para usar sua plataforma e gerencie todas em um local.
                    </p>
                  </Card.Body>
                </Card>
              </Col>

              {/* Card de Gestão Fácil */}
              <Col xs={12} md={6} lg={4} className="mb-4">
                <Card
                  className="h-100 shadow-sm border-0"
                  style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                  <div
                    style={{
                      height: "4px",
                      background: "linear-gradient(135deg, #de3096, #e83e8c)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  ></div>
                  <Card.Body className="p-4 text-center">
                    <div className="mb-3">
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          background:
                            "linear-gradient(135deg, #de3096, #e83e8c)",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          boxShadow: "0 4px 12px rgba(222, 48, 150, 0.3)",
                          margin: "0 auto",
                        }}
                      >
                        <IconWorld size={24} />
                      </div>
                    </div>
                    <h5 className="fw-bold mb-3">Gestão Centralizada</h5>
                    <p className="text-muted mb-0">
                      Painel administrativo intuitivo para gerenciar todas as
                      empresas, usuários e configurações.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>

          {/* Modal de Criação */}
          <Modal
            show={showCreateModal}
            onHide={() => setShowCreateModal(false)}
            size="lg"
            centered
          >
            <CreateOrganization
              isModal={true}
              onClose={() => setShowCreateModal(false)}
              onSuccess={handleOrganizationCreated}
            />
          </Modal>
        </div>
      );
    } else {
      // Estado padrão: usuário não pode criar organização
      return (
        <Alert variant="warning" className="organizations-dashboard__alert">
          <IconBuilding size={16} className="me-2" />
          Esta funcionalidade está disponível apenas para organizações.
        </Alert>
      );
    }
  }

  // Aguardar contexto carregar antes de exibir alerta de erro
  if (!accountContextLoaded || loading) {
    return null;
  }

  if (!organizationData) {
    return (
      <Alert variant="warning" className="organizations-dashboard__alert">
        <IconBuilding size={16} className="me-2" />
        Não foi possível carregar os dados da organização.
      </Alert>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
      }}
    >
      {/* Header Hero Section */}
      <div
        className="rounded p-4 mb-5"
        style={{
          background: "var(--gradient-highlight)",
          color: "white",
        }}
      >
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <h1
                className="mb-3 d-flex align-items-center text-capitalize"
                style={{ fontSize: "2.5rem", fontWeight: 700 }}
              >
                <IconBuilding size={40} className="me-3" />
                {organizationData.name}
              </h1>
              <p className="mb-0 text-capitalize">
                {organizationData.brand_name
                  ? `Marca: ${organizationData.brand_name}`
                  : "Gerencie sua organização e configurações"}
              </p>
            </Col>
            <Col md={4} className="text-end">
              <div className="d-flex justify-content-end gap-3">
                <div
                  style={{
                    width: 60,
                    height: 60,
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <IconStar size={24} />
                </div>
                <div
                  style={{
                    width: 70,
                    height: 70,
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <IconShield size={28} />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Cards Section */}
      <Container>
        <Row>
          {/* Card de Informações Básicas */}
          <Col xs={12} md={6} lg={6} className="mb-4">
            <Card
              className="h-100 shadow-sm border-0"
              style={{ borderRadius: "15px", overflow: "hidden" }}
            >
              <div
                style={{
                  height: "4px",
                  background: "var(--gradient-primary)",
                  borderRadius: "15px 15px 0 0",
                }}
              ></div>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: "var(--gradient-primary)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      boxShadow: "0 4px 12px rgba(48, 92, 222, 0.3)",
                    }}
                  >
                    <IconBuilding size={24} />
                  </div>
                  <h5 className="mb-0 ms-3 fw-bold">Informações Básicas</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="ms-auto"
                    onClick={() => setShowEditModal(true)}
                  >
                    <IconEdit size={14} className="me-1" />
                    Editar
                  </Button>
                </div>

                <div className="mb-3 d-flex justify-content-between align-items-center py-2 border-bottom">
                  <small className="fw-semibold text-muted">
                    Nome da Organização:
                  </small>
                  <span className="fw-semibold text-end">
                    {organizationData.name}
                  </span>
                </div>

                <div className="mb-3 d-flex justify-content-between align-items-center py-2 border-bottom">
                  <small className="fw-semibold text-muted">Nome da Marca:</small>
                  <span className="fw-semibold text-end">
                    {organizationData.brand_name || "Não definido"}
                  </span>
                </div>

                <div className="mb-3 d-flex justify-content-between align-items-center py-2 border-bottom">
                  <small className="fw-semibold text-muted">Status:</small>
                  {renderStatus(organizationData.status)}
                </div>

                <div className="mb-3 d-flex justify-content-between align-items-center py-2 border-bottom">
                  <small className="fw-semibold text-muted">White Label:</small>
                  <Badge
                    bg={
                      isRestrictedPlan()
                        ? "secondary"
                        : organizationData.is_white_label_enabled
                        ? "success"
                        : "secondary"
                    }
                  >
                    {isRestrictedPlan() ? (
                      <>
                        <IconX size={12} className="me-1" />
                        Inativo
                      </>
                    ) : (
                      <>
                        <IconCheck size={12} className="me-1" />
                        {organizationData.is_white_label_enabled
                          ? "Ativo"
                          : "Inativo"}
                      </>
                    )}
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2">
                  <small className="fw-semibold text-muted">Criado em:</small>
                  <small>
                    {format(
                      new Date(organizationData.created_at),
                      "dd/MM/yyyy",
                      { locale: ptBR }
                    )}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Card de URLs e Domínios */}
          <Col xs={12} md={6} lg={6} className="mb-4">
            <Card
              className="h-100 shadow-sm border-0"
              style={{ borderRadius: "15px", overflow: "hidden" }}
            >
              <div
                style={{
                  height: "4px",
                  background: "linear-gradient(135deg, #2bc155, #20c997)",
                  borderRadius: "15px 15px 0 0",
                }}
              ></div>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: "linear-gradient(135deg, #2bc155, #20c997)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      boxShadow: "0 4px 12px rgba(43, 193, 85, 0.3)",
                    }}
                  >
                    <IconWorld size={24} />
                  </div>
                  <h5 className="mb-0 ms-3 fw-bold">URLs e Domínios</h5>
                </div>

                {isRestrictedPlan() ? (
                  <div className="text-center py-4">
                    <IconX size={32} className="text-muted mb-2" />
                    <p className="text-muted mb-0 fw-semibold">Não elegível</p>
                    <small className="text-muted">
                      Este plano não possui acesso a domínios personalizados
                    </small>
                  </div>
                ) : organizationData.domain_url ? (
                  <div>
                    <small className="fw-semibold text-muted d-block mb-2">
                      Domínio Customizado:
                    </small>
                    <div
                      style={{
                        background: "#f8f9fa",
                        borderRadius: "8px",
                        padding: "12px",
                      }}
                    >
                      <div
                        className="d-flex align-items-center mb-2"
                        style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                      >
                        <IconGlobe size={14} className="me-2 text-primary" />
                        <span style={{ wordBreak: "break-all" }}>
                          {organizationData.domain_url}
                        </span>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            copyUrlToClipboard(organizationData.domain_url)
                          }
                        >
                          <IconCopy size={12} />
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            openExternalUrl(organizationData.domain_url)
                          }
                        >
                          <IconExternalLink size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : organizationData.subdomain ? (
                  <div>
                    <small className="fw-semibold text-muted d-block mb-2">
                      Subdomínio:
                    </small>
                    <div
                      style={{
                        background: "#f8f9fa",
                        borderRadius: "8px",
                        padding: "12px",
                      }}
                    >
                      <div
                        className="d-flex align-items-center mb-2"
                        style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                      >
                        <IconGlobe size={14} className="me-2 text-primary" />
                        <span style={{ wordBreak: "break-all" }}>
                          {organizationData.subdomain}.mercadoagentes.com
                        </span>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            copyUrlToClipboard(
                              `https://${organizationData.subdomain}.mercadoagentes.com`
                            )
                          }
                        >
                          <IconCopy size={12} />
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            openExternalUrl(
                              `https://${organizationData.subdomain}.mercadoagentes.com`
                            )
                          }
                        >
                          <IconExternalLink size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <IconX size={32} className="text-muted mb-2" />
                    <p className="text-muted mb-0">
                      {[3, 4].includes(getUserPlan())
                        ? "Não elegível"
                        : "Nenhum domínio configurado"}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          
        </Row>

        {/* Seção de Branding - Ocultar para plan_id = 3 ou 4 */}
        {!isRestrictedPlan() && (
          <Row className="mt-4">
            <Col xs={12}>
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 d-flex align-items-center">
                      <IconPalette size={18} className="me-2 text-primary" />
                      Personalização da Marca
                    </h5>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate("/organization/customization")}
                    >
                      <IconPalette size={16} className="me-2" />
                      Personalização
                    </Button>
                  </div>
                  <p className="mb-0 text-muted" style={{ lineHeight: 1.6 }}>
                    Configure as cores, logotipo e domínio personalizado da sua
                    organização para criar uma experiência única para seus
                    clientes.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Descrição se existir */}
        {organizationData.description && (
          <Row className="mt-4">
            <Col xs={12}>
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <h5 className="mb-3 d-flex align-items-center">
                    <IconBuilding size={18} className="me-2 text-primary" />
                    Sobre a Organização
                  </h5>
                  <p className="mb-0 text-muted" style={{ lineHeight: 1.6 }}>
                    {organizationData.description}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      {/* Modal de Branding */}
      <OrganizationBranding
        show={showBrandingModal}
        onHide={() => setShowBrandingModal(false)}
        organizationData={organizationData}
      />

      {/* Modal de Edição de Nomes */}
      <EditOrganizationModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        organizationData={organizationData}
        onSuccess={handleOrganizationUpdated}
      />
    </div>
  );
};

export default OrganizationOverview;
